import { AnalyticsService } from '@alison/analytics/client';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  TOUR_STEPS,
  TOUR_STORAGE_KEY,
  type TourHostView,
  type TourStep,
  type TourView,
} from './tourSteps.js';

const AUTO_START_DELAY_MS = 600;
const FINAL_NEXT_LABEL = 'Start using Alison';
const DEFAULT_NEXT_LABEL = 'Next';

type StartSource = 'auto' | 'help';

export interface TourHost {
  view: TourHostView;
  setView: (view: TourView) => void;
}

export interface TourOptions {
  steps?: TourStep[];
  autoStart?: boolean;
}

export interface TourApi {
  open: boolean;
  canStart: boolean;
  step: TourStep;
  index: number;
  total: number;
  steps: TourStep[];
  isFirst: boolean;
  isLast: boolean;
  nextLabel: string;
  start: () => void;
  next: () => void;
  back: () => void;
  goTo: (index: number) => void;
  skip: () => void;
}

// Walking the tour means leaving the page, and leaving a run discards the
// creatives picked for it, so it is only offered where that cannot happen.
export function canStartTour(view: TourHostView): boolean {
  return view !== 'run';
}

function readSeen(): boolean {
  try {
    return localStorage.getItem(TOUR_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSeen(): void {
  try {
    localStorage.setItem(TOUR_STORAGE_KEY, '1');
  } catch {
    return;
  }
}

export function useProductTour(host: TourHost, options: TourOptions = {}): TourApi {
  const steps = options.steps ?? TOUR_STEPS;
  const { view } = host;

  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const step = steps[index];
  if (!step) throw new Error('Tour has no steps');
  const isLast = index === steps.length - 1;

  const live = useRef({ index, step, host });
  useLayoutEffect(() => {
    live.current = { index, step, host };
  });
  const autoStarted = useRef(false);

  const goTo = useCallback(
    (target: number) => {
      const targetStep = steps[target];
      if (!targetStep) return;
      if (targetStep.view && live.current.host.view !== targetStep.view) {
        live.current.host.setView(targetStep.view);
      }
      setIndex(target);
    },
    [steps],
  );

  const close = useCallback(() => {
    writeSeen();
    setOpen(false);
  }, []);

  const begin = useCallback((source: StartSource) => {
    setIndex(0);
    setOpen(true);
    AnalyticsService.track('Tour - Started', { source });
  }, []);

  const start = useCallback(() => begin('help'), [begin]);

  const skip = useCallback(() => {
    AnalyticsService.track('Tour - Skipped', {
      step: live.current.step.key,
      index: live.current.index,
    });
    close();
  }, [close]);

  const next = useCallback(() => {
    const current = live.current;
    if (current.index >= steps.length - 1) {
      AnalyticsService.track('Tour - Completed');
      close();
      if (current.host.view !== 'home') current.host.setView('home');
      return;
    }
    goTo(current.index + 1);
  }, [steps.length, close, goTo]);

  const back = useCallback(() => goTo(Math.max(0, live.current.index - 1)), [goTo]);

  useEffect(() => {
    if (!open) return;
    AnalyticsService.track('Tour - Step Viewed', { step: step.key, index });
  }, [open, step.key, index]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') skip();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, skip]);

  useEffect(() => {
    if (options.autoStart === false || autoStarted.current) return;
    if (view !== 'home' || readSeen()) return;
    const timer = window.setTimeout(() => {
      autoStarted.current = true;
      begin('auto');
    }, AUTO_START_DELAY_MS);
    return () => clearTimeout(timer);
  }, [options.autoStart, view, begin]);

  // Everything under the provider consumes this, and the provider re-renders
  // whenever the app's fetch count moves.
  return useMemo(
    () => ({
      open,
      canStart: canStartTour(view),
      step,
      index,
      total: steps.length,
      steps,
      isFirst: index === 0,
      isLast,
      nextLabel: step.nextLabel ?? (isLast ? FINAL_NEXT_LABEL : DEFAULT_NEXT_LABEL),
      start,
      next,
      back,
      goTo,
      skip,
    }),
    [open, view, step, index, steps, isLast, start, next, back, goTo, skip],
  );
}
